import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Alert,
    Box,
    Typography,
    Divider,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import GroupIcon from '@mui/icons-material/Group';
import InfoIcon from '@mui/icons-material/Info';

export default function FamilyMergeConfirmationDialog({
    open,
    onClose,
    primaryCustomer,
    selectedCustomer,
    onConfirm,
    isLoading = false
}) {
    const primaryFamilySize = primaryCustomer?.familySize || 1;
    const selectedFamilySize = selectedCustomer?.familySize || 1;
    const totalSize = (primaryFamilySize || 1) + (selectedFamilySize || 1);

    const isPrimaryFamily = primaryCustomer?.familyGroupId !== null && primaryCustomer?.familyGroupId !== undefined;
    const isSelectedFamily = selectedCustomer?.familyGroupId !== null && selectedCustomer?.familyGroupId !== undefined;
    const isMergingTwoFamilies = isPrimaryFamily && isSelectedFamily && primaryCustomer?.familyGroupId !== selectedCustomer?.familyGroupId;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningIcon color="warning" />
                Confirm Family Merge
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
                {isMergingTwoFamilies ? (
                    <>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            You are about to merge two separate family groups. This action will link all members together.
                        </Alert>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                                Family Group 1: {primaryCustomer?.fullName}
                            </Typography>
                            <Box sx={{ ml: 2, mb: 2 }}>
                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                    <GroupIcon fontSize="small" color="primary" />
                                    <Typography variant="body2">
                                        {primaryFamilySize} member{primaryFamilySize !== 1 ? 's' : ''}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                                Family Group 2: {selectedCustomer?.fullName}
                            </Typography>
                            <Box sx={{ ml: 2, mb: 2 }}>
                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                    <GroupIcon fontSize="small" color="primary" />
                                    <Typography variant="body2">
                                        {selectedFamilySize} member{selectedFamilySize !== 1 ? 's' : ''}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ 
                            p: 2, 
                            backgroundColor: 'action.hover', 
                            borderRadius: 1,
                            mb: 2
                        }}>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <GroupIcon color="success" />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    Result: Family of {totalSize}
                                </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                                All members from both groups will be linked together
                            </Typography>
                        </Box>

                        <Alert severity="info" icon={<InfoIcon />}>
                            <Typography variant="caption">
                                All family members will share the same family group. Insurance policies and preferences may need to be reviewed.
                            </Typography>
                        </Alert>
                    </>
                ) : (
                    <>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {isSelectedFamily 
                                ? `${selectedCustomer?.fullName} is already part of a family group with ${selectedFamilySize} member${selectedFamilySize !== 1 ? 's' : ''}.`
                                : `${selectedCustomer?.fullName} will be linked to ${primaryCustomer?.fullName}'s family group.`
                            }
                        </Alert>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                <strong>Primary Customer:</strong>
                            </Typography>
                            <Box sx={{ ml: 2, mb: 2 }}>
                                <Typography variant="body2">{primaryCustomer?.fullName}</Typography>
                                {isPrimaryFamily && (
                                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                                        <GroupIcon fontSize="small" color="primary" />
                                        <Typography variant="caption">
                                            Family of {primaryFamilySize}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                <strong>New Member:</strong>
                            </Typography>
                            <Box sx={{ ml: 2, mb: 2 }}>
                                <Typography variant="body2">{selectedCustomer?.fullName}</Typography>
                                {isSelectedFamily && (
                                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                                        <GroupIcon fontSize="small" color="primary" />
                                        <Typography variant="caption">
                                            Currently in family of {selectedFamilySize}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} disabled={isLoading}>
                    Cancel
                </Button>
                <Button
                    onClick={onConfirm}
                    variant="contained"
                    color="warning"
                    disabled={isLoading}
                >
                    {isLoading ? 'Linking...' : 'Confirm & Link'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
